import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { useI18n } from "@/i18n";
import { http, formatApiError } from "@/lib/api";
import { COMPANY } from "@/content/company";
import { Reveal } from "./Reveal";

const EMPTY = { name: "", email: "", organisation: "", topic: "events", message: "", consent: false, website: "" };

const Field = ({ id, label, children }) => (
  <label htmlFor={id} className="block">
    <span className="eyebrow">{label}</span>
    {children}
  </label>
);

const ContactForm = () => {
  const { t, lang } = useI18n();
  const c = t.contact;
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 10 || !form.consent) {
      setError(c.required);
      return;
    }
    setStatus("sending");
    setError("");
    try {
      await http.post("/contact", { ...form, lang });
      setStatus("sent");
      setForm(EMPTY);
      toast.success(c.success);
    } catch (err) {
      setStatus("idle");
      const msg = formatApiError(err?.response?.data?.detail, c.error);
      setError(msg);
      toast.error(msg);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="contact-form" className="grid gap-8 sm:grid-cols-2">
      <Field id="cf-name" label={`${c.fields.name} *`}><input id="cf-name" data-testid="contact-name-input" className="field" autoComplete="name" value={form.name} onChange={set("name")} maxLength={120} required /></Field>
      <Field id="cf-email" label={`${c.fields.email} *`}><input id="cf-email" type="email" data-testid="contact-email-input" className="field" autoComplete="email" value={form.email} onChange={set("email")} required /></Field>
      <Field id="cf-org" label={c.fields.org}><input id="cf-org" data-testid="contact-org-input" className="field" autoComplete="organization" value={form.organisation} onChange={set("organisation")} maxLength={160} /></Field>
      <Field id="cf-topic" label={c.fields.topic}>
        <select id="cf-topic" data-testid="contact-topic-select" className="field cursor-pointer" value={form.topic} onChange={set("topic")}>
          {Object.entries(c.topics).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field id="cf-message" label={`${c.fields.message} *`}><textarea id="cf-message" data-testid="contact-message-input" rows={5} className="field resize-none" value={form.message} onChange={set("message")} maxLength={5000} required /></Field>
      </div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")} className="absolute -left-[9999px] h-0 w-0 opacity-0" />
      <label className="flex items-start gap-3 text-sm leading-relaxed text-zinc-300 sm:col-span-2">
        <input type="checkbox" data-testid="contact-consent-checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 shrink-0 accent-[#FF5500]" />
        <span>{c.consent} <Link to="/legal/privacy" data-testid="contact-privacy-link" className="text-bone underline decoration-signal underline-offset-4 hover:text-signal">{c.consentLink}</Link> {c.consentEnd}</span>
      </label>
      <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
        <p role="status" data-testid="contact-status" className={`text-sm ${error ? "text-red-400" : "text-signal"}`}>{error || (status === "sent" ? c.success : "")}</p>
        <button type="submit" disabled={status === "sending"} data-testid="contact-submit-button" className="group flex w-full items-center justify-center gap-3 rounded-full bg-signal px-8 py-4 font-medium text-ink transition-[transform,opacity] duration-300 hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto">
          {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {status === "sending" ? c.sending : c.submit}
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
        </button>
      </div>
    </form>
  );
};

export const Contact = () => {
  const { t } = useI18n();
  const c = t.contact;
  const details = [
    [c.details.address, COMPANY.address],
    [c.details.email, COMPANY.email],
    [c.details.phone, COMPANY.phone],
    [c.details.registry, COMPANY.registryCode],
  ];
  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden border-t border-white/10 py-24 lg:py-40">
      <div aria-hidden className="pointer-events-none absolute -bottom-60 right-0 h-[560px] w-[560px] rounded-full bg-signal/10 blur-[150px]" />
      <div className="wrap relative grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal><p className="eyebrow flex items-center gap-3"><span className="h-px w-8 bg-signal" />{c.label}</p></Reveal>
          <Reveal delay={0.05}><h2 className="mt-6 font-display text-4xl font-semibold leading-[0.95] tracking-[-0.03em] sm:text-5xl lg:text-6xl">{c.title}</h2></Reveal>
          <Reveal delay={0.1}><p className="mt-8 max-w-md text-zinc-300">{c.intro}</p></Reveal>
          <dl data-testid="contact-details" className="mt-12 space-y-5">
            {details.map(([k, v]) => (
              <Reveal key={k} className="border-t border-white/10 pt-4">
                <dt className="eyebrow">{k}</dt>
                <dd className="mt-1.5 text-bone">{v}</dd>
              </Reveal>
            ))}
          </dl>
        </div>
        <Reveal delay={0.1} className="lg:col-span-7"><ContactForm /></Reveal>
      </div>
    </section>
  );
};
